<!--
// app/pages/[region]/stats/teams/Cards.vue
-->
<script setup lang="ts">
import type { GroupedTeamRanking } from "~/src/stats/domain/models/Rankings";
import { useSeasonFilter } from "~/app/composables/season/seasonFilter";
import { useWebContext } from "~/app/composables/webContext";
import statsService from "~/src/stats/domain/services/statsService";
import SeasonsFilter from "~/app/components/Shared/SeasonsFilter.vue";
import HeaderBannerTitle from "~/app/components/Shared/HeaderBannerTitle.vue";
import SidebarMenuStats from "~/app/components/pages/region-stats/SidebarMenuStats.vue";
import BaseBackdropLoading from "~/app/components/base/BaseBackdropLoading.vue";
import StatsDataTable from "~/app/components/pages/region-stats/StatsDataTable.vue";
import ButtonSelectTypeStats from "~/app/components/pages/region-stats/ButtonSelectTypeStats.vue";
import { $SEASONS_WITHOUT_DATA } from "~/src/season-cluster/infrastructure/ui-services/seasonUiService";

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: "/[region]/stats/teams/cards",
    es: "/[region]/estadisticas/equipos/tarjetas",
    de: "/[region]/statistiken/mannschaften/karten",
    fr: "/[region]/statistiques/equipes/cartons",
    it: "/[region]/statistiche/squadre/cartellini",
    pt: "/[region]/estatisticas/times/cartoes",
    ar: "/[region]/stats/teams/cards",
  },
});

definePageMeta({
  middleware: ["not-found-king-of-kings-nations"],
});

const { seasonId, seasonClusterId } = useSeasonFilter();
const { mergeRankingsByPlayerId } = statsService();
const { webContextGenders } = useWebContext();
const { t } = useI18n();
const localePath = useLocalePath();
const tableStatsKey = ref<number>(0);
const sidebarKey = ref<number>(0);
const loadingSeasonData = ref<boolean>(false);

const errorMessage = ref<boolean>(false);
const tableDataTeams = ref<GroupedTeamRanking[] | undefined>([]);

const mainStat = ref<string>("CRT-G");
const otherStats = ref<string>("CRT-R,PG");
const fallbackMainStat: string = "CRT-G";
const otherStatsTeams = ref<string>("CRT-R,PG");
const allStatsTeams: string[] = ["CRT-G", "CRT-R", "PG"];

const getSeasonData = async (): Promise<void> => {
  if ($SEASONS_WITHOUT_DATA.includes(Number(seasonId.value))) {
    await navigateTo(
      localePath({ name: "region-stats", query: { season: seasonClusterId.value, split: seasonId.value } })
    );
  } else {
    try {
      loadingSeasonData.value = true;
      errorMessage.value = false;

      const seasonIdValue = seasonId.value;

      const { data } = await useFetch("/api/stats", {
        query: {
          season: seasonIdValue,
          type: "teams",
          mainCode: mainStat.value,
          secondaryCodes: otherStatsTeams.value,
          orderBy: "ranking",
          sortOrder: "ASC",
          ctxGenders: webContextGenders,
        },
      });
      tableDataTeams.value = data.value || [];
    } catch (e) {
      errorMessage.value = true;
    } finally {
      loadingSeasonData.value = false;
      tableStatsKey.value++;
      sidebarKey.value++;
    }
  }
};

await nextTick();
await getSeasonData();

useSeoMeta({
  title:
    t("header.navCompetition.stats") +
    " " +
    t("header.navCompetition.statsTeams") +
    " - " +
    t("stats.sidebar.menu.Cards"),
  ogTitle: t("header.navCompetition.stats") + " - " + t("stats.sidebar.menu.Cards"),
});

const { sortStatsTable } = statsService();

const executeSortTeams = async sortValue => {
  const codeSortValue = sortValue.sortField.split(".")[1];
  const sortOrderValue = sortValue.sortOrder;

  mainStat.value = codeSortValue;
  tableDataTeams.value = await sortStatsTable(
    codeSortValue,
    allStatsTeams,
    fallbackMainStat,
    seasonId.value,
    "teams",
    sortOrderValue
  );
};

const dataFieldsTeams = computed(() => {
  return {
    ranking: `rankingData.${mainStat.value}.ranking`,
    name: "team",
    sortField: "rankingData.CRT-G.total",
    stats: [
      { stat: "rankingData.PG.total", header: t("PJ"), sortable: false, tooltip: t("tooltip.PJ") },
      { stat: "rankingData.CRT-G.total", header: t("T.A."), sortable: true, tooltip: t("tooltip.T.A.") },
      { stat: "rankingData.CRT-R.total", header: t("T.R."), sortable: true, tooltip: t("tooltip.T.R.") },
    ],
  };
});
</script>

<template>
  <header-banner-title
    :title="$t('general.stats.title') + ' ' + $t('header.navCompetition.statsTeams')"
  ></header-banner-title>
  <section class="px-4 md:px-6">
    <div
      class="kql-w-container flex flex-col md:flex-row items-stretch sm:items-end justify-center md:items-center md:justify-between flex-wrap"
    >
      <button-select-type-stats data-type="teams" page="Cards"></button-select-type-stats>
      <seasons-filter
        v-model:seasonClusterId="seasonClusterId"
        v-model:seasonId="seasonId"
        @change="getSeasonData"
        combined-with-more-filters
      />
    </div>
  </section>

  <section class="px-4 md:px-6 my-9">
    <div class="kql-w-container grid grid-cols-1 lg:grid-cols-4 py-9 px-4 bg-gray-900 rounded-lg lg:gap-4">
      <div class="mb-9 lg:mb-0 relative">
        <sidebar-menu-stats
          :new-stats="!$SEASONS_WITHOUT_DATA.includes(Number(seasonId))"
          :key="sidebarKey"
          :season-id="seasonId"
          is-teams
        ></sidebar-menu-stats>
      </div>
      <div class="col-span-3 relative">
        <base-backdrop-loading :active="loadingSeasonData"></base-backdrop-loading>

        <div v-if="errorMessage">
          {{ $t("stats.message.notAvailable") }}
        </div>

        <div v-else class="stats-table-data max-w-full overflow-hidden" :key="tableStatsKey">
          <stats-data-table
            :main-stat="mainStat"
            :table-data="tableDataTeams"
            :data-fields="dataFieldsTeams"
            @sort="e => executeSortTeams(e)"
          ></stats-data-table>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped></style>
